import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { MlmShell } from '../components/mlm/MlmShell';
import { MlmOverview } from '../components/mlm/MlmOverview';
import { MlmProspects } from '../components/mlm/MlmProspects';
import { MlmContent } from '../components/mlm/MlmContent';
import { MlmEmail } from '../components/mlm/MlmEmail';
import { MlmAynara } from '../components/mlm/MlmAynara';
import { MlmSales } from '../components/mlm/MlmSales';
import { MlmTeam } from '../components/mlm/MlmTeam';
import { MlmActivation } from '../components/mlm/MlmActivation';
import { MlmConnections } from '../components/mlm/MlmConnections';
import { MlmMassCampaigns } from '../components/mlm/MlmMassCampaigns';
import { MlmMedia } from '../components/mlm/MlmMedia';
import { MlmAdminOnboarding } from '../components/mlm/MlmAdminOnboarding';
import PaisesCampaignHub from '../components/admin/PaisesCampaignHub';
import AgenteVentasPanel from '../components/admin/AgenteVentasPanel';
import AgenteAgendaPanel from '../components/admin/AgenteAgendaPanel';
import AgenteCRMPanel from '../components/admin/AgenteCRMPanel';
import AgenteCobrosPanel from '../components/admin/AgenteCobrosPanel';
import { KeyRound, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED = ['admin', 'mlm_distribuidor'];

// ── Forced password change modal ──────────────────────────────────────────────
function ForcePasswordChangeModal({ onSuccess, authFetch }) {
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error('La contraseña debe tener al menos 8 caracteres');
    if (pwd !== confirm) return toast.error('Las contraseñas no coinciden');
    setBusy(true);
    try {
      const res = await authFetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: pwd }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cambiar la contraseña');
      toast.success('¡Contraseña actualizada! Bienvenida a tu panel.');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="force-password-modal">
      <div className="mx-4 w-full max-w-md rounded-[28px] bg-white p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600/10">
            <KeyRound className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-900">Crea tu contraseña</h2>
            <p className="text-sm text-slate-500">Por seguridad, cambia la contraseña temporal</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Nueva contraseña</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={pwd}
                onChange={e => setPwd(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 pr-11 text-sm outline-none focus:border-blue-500 focus:bg-white"
                autoFocus
                data-testid="force-pwd-input"
              />
              <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-3 text-slate-400">
                {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Confirmar contraseña</label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repite la contraseña"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white"
              data-testid="force-pwd-confirm"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="mt-2 w-full rounded-full bg-blue-600 py-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-50"
            data-testid="force-pwd-submit"
          >
            {busy ? 'Guardando...' : 'Guardar contraseña y entrar al panel'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function MlmWorkspacePage() {
  const navigate = useNavigate();
  const { user, loading, logout, authFetch } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [section, setSection] = useState('overview');
  const [busy, setBusy] = useState('');
  // Multi-line support
  const [activeLine, setActiveLine] = useState(null); // null = use default (user.client_id)

  const load = useCallback(async (lineId = null) => {
    try {
      const url = lineId ? `${API}/mlm/workspace?line_id=${lineId}` : `${API}/mlm/workspace`;
      const res = await authFetch(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el panel');
      setWorkspace(data);
      // Auto-select first line for multi-line users
      if (data.multi_line && !lineId && data.available_lines?.length > 0) {
        const firstLine = data.available_lines[0].cid;
        setActiveLine(firstLine);
        // Reload with the first line active
        const res2 = await authFetch(`${API}/mlm/workspace?line_id=${firstLine}`);
        const data2 = await res2.json();
        if (res2.ok) setWorkspace(data2);
      }
    } catch (err) {
      toast.error(err.message);
    }
  }, [authFetch]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/acceso-mlm'); return; }
    if (!ALLOWED.includes(user.role)) { navigate(user.role === 'admin' ? '/admin' : '/panel'); return; }
    load();
  }, [loading, user, navigate, load]);

  // When activeLine changes (user switches tab), reload workspace
  const switchLine = useCallback(async (lineId) => {
    setActiveLine(lineId);
    setSection('overview');
    await load(lineId);
  }, [load]);

  const api = useCallback(async (path, options = {}, { success, key = path, silent } = {}) => {
    setBusy(key);
    try {
      // Append line_id to all API calls when in multi-line mode
      const lineParam = activeLine ? `${path.includes('?') ? '&' : '?'}line_id=${activeLine}` : '';
      const res = await authFetch(`${API}/mlm${path}${lineParam}`, { ...options, headers: { 'Content-Type': 'application/json', ...(options.headers || {}) } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error en la operación');
      if (success) toast.success(success);
      if (!silent) await load(activeLine);
      return data;
    } catch (err) {
      toast.error(err.message);
      throw err;
    } finally {
      setBusy('');
    }
  }, [authFetch, load, activeLine]);

  if (loading || !workspace) {
    return (
      <main className="min-h-screen bg-[#f3f6fb] px-4 py-10" data-testid="mlm-workspace-loading">
        <div className="mx-auto max-w-5xl rounded-[28px] border border-[#0B3F8C]/10 bg-white p-10 text-center text-slate-500">Cargando tu panel…</div>
      </main>
    );
  }

  const panelProps = { workspace, api, busy, setSection };
  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#dbe7ff_0%,_#f3f6fb_45%,_#fff5f5_100%)] px-4 py-6 sm:px-6 lg:px-8" data-testid="mlm-workspace-page">
      {workspace.user?.must_change_password && (
        <ForcePasswordChangeModal
          authFetch={authFetch}
          onSuccess={() => load(activeLine)}
        />
      )}
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex justify-end gap-3">
          {user?.role === 'admin' && <button type="button" onClick={() => navigate('/admin')} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700" data-testid="mlm-back-admin-button">Volver a admin</button>}
          <button type="button" onClick={logout} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700" data-testid="mlm-logout-button">Salir</button>
        </div>
        <MlmShell workspace={workspace} activeSection={section} onSectionChange={setSection} activeLine={activeLine} onSwitchLine={switchLine} />
        {section === 'overview' && <MlmOverview {...panelProps} />}
        {section === 'prospects' && <MlmProspects {...panelProps} />}
        {section === 'content' && <MlmContent {...panelProps} />}
        {section === 'email' && <MlmEmail {...panelProps} />}
        {section === 'mass' && <MlmMassCampaigns {...panelProps} />}
        {section === 'paises' && (
          <div className="bg-[#060D1F] rounded-[28px] p-2">
            <PaisesCampaignHub authFetch={authFetch} />
          </div>
        )}
        {section === 'aynara' && <MlmAynara {...panelProps} />}
        {section === 'sales' && <MlmSales {...panelProps} />}
        {section === 'agente_ventas' && (
          <div className="p-4"><AgenteVentasPanel authFetch={authFetch} /></div>
        )}
        {section === 'agente_agenda' && (
          <div className="p-4"><AgenteAgendaPanel authFetch={authFetch} /></div>
        )}
        {section === 'agente_crm' && (
          <div className="p-4"><AgenteCRMPanel authFetch={authFetch} /></div>
        )}
        {section === 'agente_cobros' && (
          <div className="p-4"><AgenteCobrosPanel authFetch={authFetch} /></div>
        )}
        {section === 'team' && <MlmTeam {...panelProps} />}
        {section === 'media' && <MlmMedia {...panelProps} />}
        {section === 'connections' && <MlmConnections {...panelProps} />}
        {section === 'activation' && <MlmActivation {...panelProps} />}
        {section === 'admin' && user?.role === 'admin' && <MlmAdminOnboarding {...panelProps} />}
      </div>
    </main>
  );
}
